import { NewPasswordFields } from "@/components/account/NewPasswordFields";
import { StandaloneScreenFooter } from "@/components/layout/StandaloneScreenFooter";
import { AvatarBox } from "@/components/logo";
import {
  analytics,
  PASSWORD_SET_ACTIVATION_SOURCE,
  PASSWORD_SET_GATE_SOURCE,
  PII_MASK_CLASS
} from "@/lib/analytics";
import { readSessionActor } from "@/lib/analytics/actor";
import { handleActionError } from "@/lib/errors";
import { firstLoginContinueTarget } from "@/lib/first-login/gate";
import { HOME_PATH, SET_PASSWORD_GATE_SOURCE } from "@/lib/navigation/routes";
import { validateNewPassword } from "@/lib/password";
import { profileQuery, useProfile } from "@/queries/customer/customer";
import { useSetPassword } from "@/queries/customer/set-password";
import { queryClient } from "@/queries/queryClient";
import { Alert, Button, Heading, Text } from "@medusajs/ui";
import { createRoute, useRouter, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { authRoute } from "../$authRoute";

export const activateRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "activate",
  validateSearch: (
    search?: Record<string, unknown>
  ): { redirect?: string; source?: typeof SET_PASSWORD_GATE_SOURCE } => ({
    redirect:
      typeof search?.redirect === "string" && search.redirect
        ? search.redirect
        : undefined,
    source:
      search?.source === SET_PASSWORD_GATE_SOURCE
        ? SET_PASSWORD_GATE_SOURCE
        : undefined
  }),
  component: ActivatePage
});

const ERROR_ID = "activate-error";

/**
 * The set-password step: "Activate your account" (KAN-293) or, for a salon that
 * the first-login gate sent here, "Set a password" (KAN-306).
 *
 * Two ways in, one form:
 * - **Activation.** An approved salon lands here from the approval email:
 *   `/enter` has just redeemed the link and opened a session. No `source`.
 * - **Gate.** `authRoute` sends any passwordless salon session here before
 *   anything else (`source=gate`), with the page it was heading to in
 *   `redirect`. The gate cannot tell how the session arrived, so the copy and
 *   the analytics source are neutral.
 *
 * Setting the password is what fires `customer.password_set`, i.e. the welcome
 * email, and what retires the salon's login links server-side - the session it
 * is using keeps working, since it is a JWT.
 *
 * It sits behind `authRoute` (there is nothing to set a password *on* without a
 * session) but is exempt from the gate, which would otherwise loop. After saving
 * the salon continues to `redirect` (home when there is none) and the gate takes
 * it to the address step if one is due, carrying the same target on.
 *
 * Salon sessions only: any other session (an operator's, or one whose actor is
 * unknown) is sent on without seeing the form.
 */
function ActivatePage() {
  const router = useRouter();
  const { redirect, source } = useSearch({ from: activateRoute.id });
  const isGate = source === SET_PASSWORD_GATE_SOURCE;
  const { data: profile } = useProfile();
  const { mutateAsync: setPassword, isPending } = useSetPassword();

  const [password, setPasswordValue] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Set only by a failed validation, so the fields never look wrong before the
  // salon has tried to submit.
  const [invalid, setInvalid] = useState(false);

  // Editing a field the failed validation flagged takes the flag - and the
  // message that goes with it - away, as the login form does. A server error
  // (`invalid` false) stays until the next submit.
  const clearValidation = useCallback(() => {
    if (!invalid) return;
    setInvalid(false);
    setError(null);
  }, [invalid]);

  const hasPassword = profile?.customer?.has_password === true;

  // Only the salon activates its own account. An operator shares the salon's
  // customer session and would otherwise be handed a form that sets the salon's
  // password; a session whose actor cannot be established is treated the same
  // way, as the first-login gate does. Read once - the actor cannot change while
  // this page is open.
  const [isSalonSession] = useState(() => readSessionActor() === "salon_user");

  // Every way off this page goes through here so it navigates once, however
  // many of them fire (the effect below and a successful save both can).
  //
  // `router.navigate({ href })`, not `navigate({ to })`: the original target may
  // carry a query string (the cart's `?tab=`), which `to` does not parse. The
  // target has been resolved to an in-app path, so it stays in the SPA.
  const leftRef = useRef(false);
  const leave = useCallback(() => {
    if (leftRef.current) return;
    leftRef.current = true;
    router
      .navigate({ href: firstLoginContinueTarget(redirect), replace: true })
      .catch(() => {
        if (!isSalonSession) {
          // This page renders nothing for these sessions, so there is no form to
          // show an error on or retry from. Fall back to a full page load home
          // rather than leave them on a blank screen.
          window.location.replace(HOME_PATH);
          return;
        }
        // Let the salon try again rather than strand them on a saved form.
        leftRef.current = false;
        setError("We couldn't continue. Please try again.");
      });
  }, [router, redirect, isSalonSession]);

  // Someone who already has a password has nothing to activate: a reload after
  // saving, a bookmarked URL, or the back button. Not an error - just move on.
  useEffect(() => {
    if (!isSalonSession || hasPassword) {
      leave();
    }
  }, [isSalonSession, hasPassword, leave]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError(null);
      setInvalid(false);

      const problem = validateNewPassword(password, confirmation);
      if (problem) {
        setError(problem);
        setInvalid(true);
        return;
      }

      try {
        await setPassword(password);
      } catch (caught) {
        setError(
          handleActionError(caught, {
            boundary: "activate account",
            fallback: "We couldn't save your password. Please try again.",
            trustClientErrors: true
          })
        );
        return;
      }

      analytics.passwordSet({
        source: isGate
          ? PASSWORD_SET_GATE_SOURCE
          : PASSWORD_SET_ACTIVATION_SOURCE
      });
      // `has_password` just changed; the next screen reads the profile.
      await queryClient.invalidateQueries({
        queryKey: profileQuery().queryKey
      });
      leave();
    },
    [password, confirmation, setPassword, leave, isGate]
  );

  if (!isSalonSession) {
    return null;
  }

  return (
    <div className="bg-ui-bg-subtle flex min-h-dvh w-dvw items-center justify-center">
      <div className="m-4 flex w-full max-w-[300px] flex-col items-center">
        <AvatarBox checked={false} />
        <div className="mb-4 flex flex-col items-center text-center">
          <Heading>
            {isGate ? "Set a password" : "Activate your account"}
          </Heading>
          <Text size="small" className="text-ui-fg-subtle">
            {isGate
              ? "Set a password so you can sign in to SalonStocker any time."
              : "Choose a password to finish setting up."}
          </Text>
          {profile?.customer?.email ? (
            <Text size="small" className="text-ui-fg-subtle mt-1 max-w-full">
              You&apos;ll sign in with{" "}
              {/* On its own line, so a long address is never split by the
                  sentence around it; it breaks mid-address only if it alone is
                  wider than the column. Masked: the input masking does not
                  reach rendered text (ADR-0006). */}
              <span
                className={`text-ui-fg-base block break-words font-medium ${PII_MASK_CLASS}`}
              >
                {profile.customer.email}
              </span>
            </Text>
          ) : null}
        </div>
        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-y-4">
          <NewPasswordFields
            password={password}
            confirmation={confirmation}
            onPasswordChange={(value) => {
              clearValidation();
              setPasswordValue(value);
            }}
            onConfirmationChange={(value) => {
              clearValidation();
              setConfirmation(value);
            }}
            passwordLabel="Choose a password"
            confirmationLabel="Confirm password"
            invalid={invalid}
            describedBy={error ? ERROR_ID : undefined}
          />
          {error && (
            <Alert
              id={ERROR_ID}
              role="alert"
              className="bg-ui-bg-base items-center p-2"
              variant="error"
            >
              {error}
            </Alert>
          )}
          <Button className="w-full" type="submit" isLoading={isPending}>
            {isGate ? "Save password" : "Activate account"}
          </Button>
        </form>
        <StandaloneScreenFooter profile={profile?.customer} />
      </div>
    </div>
  );
}
